import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  #giftObserver = new MutationObserver(() => this.#syncGiftShortcut());
  #giftResizeFrame = 0;

  #giftChooser() {
    const chooser = this.querySelector('.bogos-gift-slider-view');
    return chooser instanceof HTMLElement && chooser.getClientRects().length && chooser.querySelector('button.bogos-gift-item-add-btn:not([disabled])') ? chooser : null;
  }

  #syncGiftShortcut = () => {
    const button = this.querySelector('[data-ma-choose-gifts]');
    if (button instanceof HTMLButtonElement) button.hidden = !this.#giftChooser();
  };

  #chooseGifts = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('[data-ma-choose-gifts]')) return;
    const chooser = this.#giftChooser();
    if (!chooser) return;
    const scroller = this.querySelector('.cart-drawer__items');
    if (scroller instanceof HTMLElement) scroller.scrollTo({ top: scroller.scrollTop + chooser.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    chooser.setAttribute('tabindex', '-1');
    chooser.focus({ preventScroll: true });
  };
  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#chooseGifts);
    this.#giftObserver.observe(this, { childList: true, subtree: true });
    this.#syncGiftShortcut();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#chooseGifts);
    this.#giftObserver.disconnect();
    cancelAnimationFrame(this.#giftResizeFrame);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#syncGiftShortcut();
    cancelAnimationFrame(this.#giftResizeFrame);
    this.#giftResizeFrame = requestAnimationFrame(() => {
      const card = this.#giftChooser()?.querySelector('.bogos-offer-gift-item');
      // The app can measure its slider while the native dialog is still hidden.
      if (card && card.getBoundingClientRect().width < 40) window.dispatchEvent(new Event('resize'));
      this.#syncGiftShortcut();
    });

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        if (!shouldAutoOpen || detail?.didError) {
          return;
        }

        const openDrawer = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openDrawer, { once: true });
        } else {
          openDrawer();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

class MerseyCapSelector extends Component {
  static pending = false;
  static observedAttributes = ['data-complete'];
  attributeChangedCallback(name, previous, current) {
    if (previous !== current) queueMicrotask(() => {
      const details = this.querySelector('details');
      if (details) details.open = current !== 'true';
    });
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.handleClick);
    this.resizeObserver = new ResizeObserver(this.updateArrows);
    this.bindRail();
  }
  updatedCallback() {
    super.updatedCallback();
    this.bindRail();
  }
  bindRail() {
    this.rail?.removeEventListener('scroll', this.updateArrows);
    this.details?.removeEventListener('toggle', this.updateArrows);
    this.resizeObserver?.disconnect();
    this.rail = this.querySelector('.ma-caps__rail');
    this.details = this.querySelector('details');
    this.rail?.addEventListener('scroll', this.updateArrows, { passive: true });
    this.details?.addEventListener('toggle', this.updateArrows);
    if (this.rail) this.resizeObserver?.observe(this.rail);
    this.updateArrows();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleClick);
    this.rail?.removeEventListener('scroll', this.updateArrows);
    this.details?.removeEventListener('toggle', this.updateArrows);
    this.resizeObserver?.disconnect();
  }
  updateArrows = () => {
    if (!this.rail) return;
    const previous = this.querySelector('[data-direction="-1"]');
    const next = this.querySelector('[data-direction="1"]');
    if (previous) previous.disabled = this.rail.scrollLeft <= 2;
    if (next) next.disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 2;
  };
  handleClick = async (event) => {
    if (!(event.target instanceof Element)) return;
    const arrow = event.target.closest('[data-direction]');
    if (arrow) {
      this.rail?.scrollBy({ left: Number(arrow.dataset.direction) * (this.rail.clientWidth - 6), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      return;
    }
    const button = event.target.closest('button[data-variant]');
    if (!button?.dataset.variant || MerseyCapSelector.pending) return;
    MerseyCapSelector.pending = true;
    const label = button.textContent;
    this.querySelectorAll('button[data-variant]').forEach(el => { el.disabled = true; });
    button.textContent = this.dataset.loading;
    const message = this.querySelector('.ma-caps__message');
    message.hidden = true;
    const deferred = CartLinesUpdateEvent.createPromise();
    const variantId = button.dataset.variant;
    const root = window.Shopify?.routes?.root || '/';
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: variantId, quantity: 1 }], promise: deferred.promise }));
    let added = false;
    try {
      const sections = [...new Set(['cart-drawer-section', ...Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), el => el.dataset.sectionId)])].slice(0, 5);
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections, sections_url: window.location.pathname }) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
      added = true;
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error(this.dataset.error);
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: result.sections, items: cart.items, itemCount: 1, source: this.localName, productId: button.dataset.product, didError: false } });
      message.textContent = this.dataset.added;
      message.hidden = false;
    } catch (error) {
      deferred.resolve({ detail: { didError: true } });
      message.textContent = added ? this.dataset.error : error.message || this.dataset.error;
      message.hidden = false;
    } finally {
      MerseyCapSelector.pending = false;
      button.textContent = label;
      this.querySelectorAll('button[data-variant]').forEach(el => { el.disabled = !el.dataset.variant; });
      document.querySelectorAll('ma-cart-upsell').forEach(el => el.syncVariants());
    }
  };
}

if (!customElements.get('ma-cap-selector')) customElements.define('ma-cap-selector', MerseyCapSelector);

class MerseyCartUpsell extends MerseyCapSelector {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.selectVariant);
    if (this.hasAttribute('data-product-upsell')) document.addEventListener(StandardEvents.cartLinesUpdate, this.hideAddedProducts);
    this.syncVariants();
  }
  updatedCallback() {
    super.updatedCallback();
    this.syncVariants();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.selectVariant);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.hideAddedProducts);
  }
  hideAddedProducts = (event) => {
    event.promise?.then(({ detail }) => {
      if (!this.isConnected || !detail?.items || detail.didError) return;
      const ids = new Set(detail.items.map(item => String(item.product_id)));
      const cards = [...this.querySelectorAll('[data-upsell-product]')];
      cards.forEach(card => { card.hidden = ids.has(card.dataset.upsellProduct); });
      this.hidden = cards.every(card => card.hidden);
      if (event.action !== 'add') this.querySelector('.ma-caps__message').hidden = true;
    }).catch(() => {});
  };
  selectVariant = (event) => {
    if (event.target instanceof HTMLSelectElement && event.target.matches('[data-upsell-variant]')) this.syncVariants();
  };
  syncVariants() {
    for (const card of this.querySelectorAll('[data-upsell-card]')) {
      const button = card.querySelector('button[data-variant]');
      if (!button) continue;
      const select = card.querySelector('[data-upsell-variant]');
      if (select instanceof HTMLSelectElement) {
        const option = select.selectedOptions[0];
        button.dataset.variant = select.value;
        if (option?.dataset.price) card.querySelector('[data-upsell-price]').textContent = option.dataset.price;
      }
      button.disabled = !button.dataset.variant || MerseyCapSelector.pending;
    }
  }
}

if (!customElements.get('ma-cart-upsell')) customElements.define('ma-cart-upsell', MerseyCartUpsell);
